import { useEffect, useRef, useState } from 'react';
import { ExternalLink, FileSpreadsheet, Folder, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import type { SheetTable } from '@/features/manufacturing/bomExport';
import {
  createSpreadsheetInFolder,
  googleSheetsConfigError,
  openDriveFolderPicker,
  signInToGoogleDrive,
  useGoogleDriveSession,
  type CreatedGoogleSheet,
  type DriveFolder,
} from '@/features/manufacturing/googleSheets';

interface ExportBomToSheetsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTitle: string;
  tables: SheetTable[];
}

type Phase = 'form' | 'authorizing' | 'picking' | 'saving' | 'done';

const messageFrom = (err: unknown) =>
  err instanceof Error ? err.message : 'Could not save the Google Sheet.';

/**
 * Asks for a spreadsheet title, then hands the user Google's own Drive
 * popup to sign in (if needed) and pick the folder. The sheet is created
 * in that folder — nothing is downloaded.
 */
export function ExportBomToSheetsDialog({
  open,
  onOpenChange,
  defaultTitle,
  tables,
}: ExportBomToSheetsDialogProps) {
  const session = useGoogleDriveSession();
  const [title, setTitle] = useState(defaultTitle);
  const [folder, setFolder] = useState<DriveFolder | null>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedGoogleSheet | null>(null);
  const defaultTitleRef = useRef(defaultTitle);
  defaultTitleRef.current = defaultTitle;
  const mounted = useRef(true);
  const runId = useRef(0);
  const configError = googleSheetsConfigError();

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitleRef.current);
    setError(null);
    setCreated(null);
    setPhase('form');
  }, [open]);

  const busy = phase === 'authorizing' || phase === 'saving';

  const saveTo = async (target: DriveFolder, sheetTitle: string) => {
    const run = runId.current;
    setPhase('saving');
    setError(null);
    try {
      const current = await signInToGoogleDrive();
      const result = await createSpreadsheetInFolder({
        accessToken: current.accessToken,
        title: sheetTitle,
        folderId: target.id,
        tables,
      });
      if (!mounted.current || runId.current !== run) {
        toast.success(`Saved “${result.name}” to Google Drive`);
        return;
      }
      setCreated(result);
      setPhase('done');
    } catch (err) {
      const text = messageFrom(err);
      if (!mounted.current || runId.current !== run) {
        toast.error(text);
        return;
      }
      setError(text);
      setPhase('form');
    }
  };

  const chooseFolder = async () => {
    const sheetTitle = title.trim();
    if (!sheetTitle) {
      setError('Give the Google Sheet a title.');
      return;
    }
    const run = ++runId.current;
    setError(null);
    setCreated(null);
    setPhase('authorizing');
    // Let the button show “Waiting for Google…” before the popup takes focus.
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    if (!mounted.current || runId.current !== run) return;
    try {
      await signInToGoogleDrive();
    } catch (err) {
      if (!mounted.current || runId.current !== run) return;
      setError(messageFrom(err));
      setPhase('form');
      return;
    }
    if (!mounted.current || runId.current !== run) return;
    // Close our dialog before Google's folder popup. The Picker is an
    // overlay in this page, and a modal dialog would cover it.
    setPhase('picking');
    // Radix locks the page while the dialog is open. Wait until that lock
    // lifts so the Drive popup, which is added to document.body, can be clicked.
    for (let attempt = 0; attempt < 20; attempt += 1) {
      if (document.body.style.pointerEvents !== 'none') break;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    if (!mounted.current || runId.current !== run) return;
    try {
      const picked = await openDriveFolderPicker();
      if (!mounted.current || runId.current !== run) return;
      if (!picked) {
        setPhase('form');
        return;
      }
      setFolder(picked);
      await saveTo(picked, sheetTitle);
    } catch (err) {
      if (!mounted.current || runId.current !== run) return;
      setError(messageFrom(err));
      setPhase('form');
    }
  };

  const switchAccount = async () => {
    setError(null);
    setFolder(null);
    setPhase('authorizing');
    try {
      await signInToGoogleDrive({ selectAccount: true });
      if (!mounted.current) return;
      setPhase('form');
    } catch (err) {
      if (!mounted.current) return;
      setError(messageFrom(err));
      setPhase('form');
    }
  };

  const signedIn = !!session;

  return (
    <Dialog
      open={open && phase !== 'picking'}
      onOpenChange={(next) => {
        if (!next && phase === 'picking') return;
        if (!next) {
          runId.current += 1;
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="max-w-md">
        {phase === 'done' && created ? (
          <>
            <DialogHeader>
              <DialogTitle>Saved to Google Drive</DialogTitle>
              <DialogDescription>
                “{created.name}” is in {folder?.name ?? 'your Drive'} as a Google Sheet, with a BOM
                tab and a parts-list tab.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/40 px-3 py-3 text-sm">
              <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-info" />
              <div>
                <div className="font-medium text-foreground">{created.name}</div>
                <div className="text-muted-foreground">{folder?.name ?? 'Google Drive'}</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Done
              </Button>
              <Button asChild>
                <a href={created.url} target="_blank" rel="noopener noreferrer">
                  Open in Google Sheets
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Save to Google Sheets</DialogTitle>
              <DialogDescription>
                Sign in with Google, pick a folder in Drive, and set the title. Atlas creates the
                Sheet in that folder, with the exploded BOM and a flat parts list for this build
                quantity.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="bom-sheet-title">Title</Label>
                <Input
                  id="bom-sheet-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  disabled={busy}
                  maxLength={200}
                  autoFocus
                />
              </div>

              {folder && (
                <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                  <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 truncate text-foreground">{folder.name}</span>
                </div>
              )}

              {signedIn && session?.email && (
                <p className="text-xs text-muted-foreground">
                  Signed in as <span className="text-foreground">{session.email}</span>.{' '}
                  <button
                    type="button"
                    className="text-info hover:underline disabled:opacity-50"
                    onClick={() => void switchAccount()}
                    disabled={busy}
                  >
                    Switch account
                  </button>
                </p>
              )}

              {configError && <p className="text-sm text-destructive">{configError}</p>}
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  runId.current += 1;
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              {folder && phase === 'form' && (
                <Button
                  variant="outline"
                  onClick={() => void chooseFolder()}
                  disabled={!!configError || !title.trim()}
                >
                  Change folder
                </Button>
              )}
              <Button
                onClick={() =>
                  void (folder && phase === 'form' ? saveTo(folder, title.trim()) : chooseFolder())
                }
                disabled={!!configError || busy || !title.trim()}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {phase === 'authorizing'
                  ? 'Waiting for Google…'
                  : phase === 'saving'
                    ? 'Saving to Drive…'
                    : folder && phase === 'form'
                      ? 'Save to Drive'
                      : signedIn
                        ? 'Choose folder in Google Drive'
                        : 'Sign in and choose folder'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
